import { Row, Col } from 'antd';
import {
  InputField,
  SelectField,
  ListOptionsField,
  RadioGroupField,
} from '@/components/Form';
import {
  AGREE_OPTIONS,
  COMMISSION_FREQUENCY_OPTIONS,
  FREQUENCY_OPTIONS,
  MONTHS,
  YEARS,
} from '@/constant/options';
import { currencies } from '@/utils/currenciesUtils';
import { Control } from 'react-hook-form';
import {
  ALLOWANCES_TYPES_OPTIONS,
  COMMISSION_YEARS,
} from '../../constant/optionList';
import FormMainLabel from '@/components/Elements/Heading/FormMainLabel';
import { useEffect } from 'react';

interface EmployeeFieldsProps {
  control: Control<any>;
  errors: any;
  watchSalaryCurrency: string | undefined;
  watchAllowanceIncome: boolean | undefined;
  watchCommissionIncome: boolean | undefined;
  watchAnnualCashBonus: boolean | undefined;
  watchStockUnit: boolean | undefined;
  resetField: any;
}

export const EmployeeFields: React.FC<EmployeeFieldsProps> = ({
  control,
  errors,
  watchSalaryCurrency,
  watchAllowanceIncome,
  watchCommissionIncome,
  watchAnnualCashBonus,
  watchStockUnit,
  resetField,
}) => {
  useEffect(() => {
    if (watchAllowanceIncome === false) {
      resetField('allowanceIncome');
      resetField('allowanceFrequency');
      resetField('typeOfAllowanceIncome');
    }
  }, [watchAllowanceIncome]);

  useEffect(() => {
    if (watchCommissionIncome === false) {
      resetField('commissionIncome');
      resetField('commissionFrequency');
      resetField('commissionYears');
    }
  }, [watchCommissionIncome]);

  useEffect(() => {
    if (watchAnnualCashBonus === false) {
      resetField('thisYearAnnualCashBonus');
      resetField('lastYearAnnualCashBonus');
    }
  }, [watchAnnualCashBonus]);

  useEffect(() => {
    if (watchStockUnit === false) {
      resetField('thisYearStockOptions');
      resetField('lastYearStockOptions');
    }
  }, [watchStockUnit]);

  return (
    <Col xl={24}>
      <Row gutter={20}>
        <Col xs={24}>
          <FormMainLabel
            index={2}
            label="When did you start your current job?"
          />
        </Col>
        <Col xs={24} md={12}>
          <SelectField
            options={MONTHS}
            name="jobStartMonth"
            control={control}
            size="large"
            label="Started Month"
            placeholder="Select job start month"
            showSearch={true}
            error={errors?.jobStartMonth?.message ?? ''}
          />
        </Col>
        <Col xs={24} md={12}>
          <SelectField
            options={YEARS}
            name="jobStartYear"
            control={control}
            size="large"
            label="Started Year"
            placeholder="Select job start year"
            showSearch={true}
            error={errors?.jobStartYear?.message ?? ''}
          />
        </Col>
        <Col xs={24}>
          <FormMainLabel index={3} label="What's your Occupation?" />
        </Col>
        <Col xs={24}>
          <InputField
            name="occupation"
            control={control}
            error={errors?.occupation?.message ?? ''}
            placeholder="Enter your occupation"
            size="large"
          />
        </Col>
        <Col xs={24}>
          <FormMainLabel index={4} label="What's your base salary income?" />
        </Col>
        <Col xs={24} md={12}>
          <SelectField
            showSearch={true}
            options={currencies}
            name="salaryCurrency"
            control={control}
            size="large"
            label="Salary Currency"
            placeholder="Select your salary currency"
            error={errors?.salaryCurrency?.message ?? ''}
          />
        </Col>
        <Col xs={24} md={12}>
          <InputField
            label="Base Salary"
            name="baseSalary"
            control={control}
            error={errors?.baseSalary?.message ?? ''}
            placeholder="Enter your base salary"
            size="large"
            type="number"
            prefix={watchSalaryCurrency ?? ''}
          />
        </Col>
        <Col xs={24}>
          <RadioGroupField
            label="Frequency of your salary"
            size="large"
            name="salaryFrequency"
            options={FREQUENCY_OPTIONS}
            error={errors?.salaryFrequency?.message ?? ''}
            control={control}
          />
        </Col>
        <Col xs={24}>
          <FormMainLabel index={5} label="Do you receive allowance income?" />
          <RadioGroupField
            size="large"
            name="receiveAllowanceIncome"
            options={AGREE_OPTIONS}
            error={errors?.receiveAllowanceIncome?.message ?? ''}
            control={control}
          />
        </Col>
        {watchAllowanceIncome && (
          <>
            <Col xs={24} md={12}>
              <InputField
                label="Allowance Income"
                name="allowanceIncome"
                control={control}
                placeholder="Enter your allowance income"
                size="large"
                type="number"
                prefix={watchSalaryCurrency ?? ''}
                error={errors?.allowanceIncome?.message ?? ''}
              />
            </Col>
            <Col xs={24} md={12}>
              <SelectField
                label="Allowance Frequency"
                name="allowanceFrequency"
                placeholder="Select frequency"
                control={control}
                error={errors?.allowanceFrequency?.message ?? ''}
                size="large"
                showSearch={true}
                options={FREQUENCY_OPTIONS}
              />
            </Col>
            <Col xs={24}>
              <RadioGroupField
                label="Type of allowance income"
                multiple={true}
                size="large"
                name="typeOfAllowanceIncome"
                options={ALLOWANCES_TYPES_OPTIONS}
                error={errors?.typeOfAllowanceIncome?.message ?? ''}
                control={control}
              />
            </Col>
          </>
        )}
        <Col xs={24}>
          <FormMainLabel index={6} label="Do you receive commission income?" />
          <RadioGroupField
            size="large"
            name="receiveCommissionIncome"
            options={AGREE_OPTIONS}
            error={errors?.receiveCommissionIncome?.message ?? ''}
            control={control}
          />
        </Col>
        {watchCommissionIncome && (
          <>
            <Col xs={24} md={12}>
              <InputField
                label="What is your commission Income?"
                name="commissionIncome"
                control={control}
                placeholder="Enter your commission income"
                size="large"
                type="number"
                prefix={watchSalaryCurrency ?? ''}
                error={errors?.commissionIncome?.message ?? ''}
              />
            </Col>
            <Col xs={24} md={12}>
              <SelectField
                label="Commission Frequency"
                placeholder="Select frequency"
                name="commissionFrequency"
                control={control}
                error={errors?.commissionFrequency?.message ?? ''}
                size="large"
                showSearch={true}
                options={COMMISSION_FREQUENCY_OPTIONS}
              />
            </Col>
            <Col xs={24}>
              <SelectField
                name="commissionYears"
                options={COMMISSION_YEARS}
                control={control}
                size="large"
                label="How long have you been receiving this commission?"
                placeholder="Select years"
                error={errors?.commissionYears?.message ?? ''}
              />
            </Col>
          </>
        )}
        <Col xs={24}>
          <FormMainLabel index={7} label="Do you receive Annual Cash Bonus?" />
          <RadioGroupField
            name="receiveAnnualCashBonus"
            options={AGREE_OPTIONS}
            error={errors?.receiveAnnualCashBonus?.message ?? ''}
            control={control}
            size="large"
          />
        </Col>
        {watchAnnualCashBonus && (
          <>
            <Col xs={24} md={12}>
              <InputField
                label="This Year Cash Bonus"
                name="thisYearAnnualCashBonus"
                control={control}
                placeholder="Enter your this year annual cash bonus"
                size="large"
                type="number"
                prefix={watchSalaryCurrency ?? ''}
                error={errors?.thisYearAnnualCashBonus?.message ?? ''}
              />
            </Col>
            <Col xs={24} md={12}>
              <InputField
                label="Last Year Cash Bonus"
                name="lastYearAnnualCashBonus"
                control={control}
                placeholder="Enter your last year annual cash bonus"
                size="large"
                type="number"
                prefix={watchSalaryCurrency ?? ''}
                error={errors?.lastYearAnnualCashBonus?.message ?? ''}
              />
            </Col>
          </>
        )}
        <Col xs={24}>
          <FormMainLabel
            index={8}
            label="Do you receive stock options/restricted stock units?"
          />
          <RadioGroupField
            size="large"
            name="receiveStockUnit"
            options={AGREE_OPTIONS}
            error={errors?.receiveStockUnit?.message ?? ''}
            control={control}
          />
        </Col>
        {watchStockUnit && (
          <>
            <Col xs={24} md={12}>
              <InputField
                label="This Year Stock Options"
                name="thisYearStockOptions"
                control={control}
                placeholder="Enter your this year stock options"
                size="large"
                type="number"
                prefix={watchSalaryCurrency ?? ''}
                error={errors?.thisYearStockOptions?.message ?? ''}
              />
            </Col>
            <Col xs={24} md={12}>
              <InputField
                label="Last Year Stock Options"
                name="lastYearStockOptions"
                control={control}
                placeholder="Enter your last year stock options"
                size="large"
                type="number"
                prefix={watchSalaryCurrency ?? ''}
                error={errors?.lastYearStockOptions?.message ?? ''}
              />
            </Col>
          </>
        )}
      </Row>
    </Col>
  );
};
